import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment-timezone';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import './SrTableColumnFilter.less';

// Single source of truth for "Austin time" used by the date-range wire converters
// below. The accounting dashboard displays every date column in Austin time
// (momentWithAustinTimezone), so the operator's mental model of the From/To
// boundaries is always Austin-local — regardless of where their browser's local
// timezone happens to be set.
const AUSTIN_TZ = 'America/Chicago';

export const SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL = 'categorical';
export const SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE = 'dateRange';
export const SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE = 'numberRange';
export const SR_TABLE_COLUMN_FILTER_TYPE_TEXT = 'text';

export const SR_TABLE_COLUMN_FILTER_TYPES = [
  SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
  SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE,
  SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE,
  SR_TABLE_COLUMN_FILTER_TYPE_TEXT,
];

// Per-column hint for the date-range filter wire format. Picks the right shape
// for the underlying Postgres column type so boundary comparisons land on the
// dates the operator picked:
//
//   'date'    — DATE-typed column (no time component, no timezone). Send
//               YYYY-MM-DD strings; backend's applyRangeFilter pushes the
//               upper bound to end-of-day for inclusive matching. Comparing
//               an Austin-tz ISO timestamp against a DATE column shifts the
//               boundaries (Postgres casts the ISO to DATE in the session
//               timezone), which QA observed as "shows May 2-8 instead of
//               May 1-7" on the Move-In Date column.
//
//   'instant' — TIMESTAMPTZ-typed column (precise instant). Send Austin-tz
//               ISO timestamps so `>= start-of-day Austin` / `<= end-of-day
//               Austin` boundaries match the dates the operator picked even
//               when rows landed in UTC near the Austin midnight boundary.
//               Without this, columns like `accountRequestedAt` shifted by
//               one day for any row whose UTC date crossed the Austin
//               midnight boundary (the original QA #3 report).
export const SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_DATE = 'date';
export const SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_INSTANT = 'instant';

export const SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODES = [
  SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_DATE,
  SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_INSTANT,
];

// Trigger chrome. 'chip' is the default outlined pill. 'bare' keeps only the arrow visible,
// including when active, so the control matches the visual weight of a neighbouring sort caret.
export const SR_TABLE_COLUMN_FILTER_TRIGGER_CHIP = 'chip';
export const SR_TABLE_COLUMN_FILTER_TRIGGER_BARE = 'bare';

export const SR_TABLE_COLUMN_FILTER_TRIGGER_VARIANTS = [
  SR_TABLE_COLUMN_FILTER_TRIGGER_CHIP,
  SR_TABLE_COLUMN_FILTER_TRIGGER_BARE,
];

const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// True iff the filter `value` represents a non-empty selection. Accepts either the wire
// format committed via onChange or the in-flight draft used inside the popover, so the
// same predicate can drive both `isActive` (wire) and `apply()` (draft) checks.
function hasValue(filterType, value) {
  if (value === null || value === undefined) {
    return false;
  }
  switch (filterType) {
    case SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL:
      return Array.isArray(value) && value.length > 0;
    case SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE:
    case SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE:
      return Boolean(
        value && (value.from != null || value.to != null || value.min != null || value.max != null),
      );
    case SR_TABLE_COLUMN_FILTER_TYPE_TEXT: {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return (
        typeof value === 'object' &&
        typeof value.contains === 'string' &&
        value.contains.trim().length > 0
      );
    }
    default:
      return false;
  }
}

// Convert a wire-format date-range value (Austin-timezone ISO timestamps OR
// YYYY-MM-DD strings, depending on column type) back into the YYYY-MM-DD strings
// that the <input type="date"> element needs. Same unwrap path works for both
// modes: ISO strings are formatted in Austin time; YYYY-MM-DD strings pass
// through. So the unwrap doesn't need to know the dateInputMode.
function dateRangeFromWire(value) {
  if (!value || typeof value !== 'object') return value;
  const toDateInput = (raw) => {
    if (raw == null || raw === '') return raw;
    if (typeof raw === 'string' && ISO_DATE_ONLY_PATTERN.test(raw)) return raw;
    // Format the instant in Austin time so an ISO like 2026-05-01T05:00:00Z
    // (which is May 1 00:00 Austin) round-trips back to '2026-05-01' rather
    // than '2026-04-30' (which it would in UTC).
    const m = momentWithAustinTimezone(raw);
    return m.isValid() ? m.format('YYYY-MM-DD') : raw;
  };
  return {
    ...(value.from !== undefined ? { from: toDateInput(value.from) } : {}),
    ...(value.to !== undefined ? { to: toDateInput(value.to) } : {}),
  };
}

// Convert the date-range draft (YYYY-MM-DD strings from <input type="date">) into the
// shape sent over the wire: ISO timestamps anchored to Austin time so the backend's
// `>= lower` / `<= upper` comparisons align with the dates the user actually picked.
//
// Without this, the backend treats `from: '2026-05-01'` as `2026-05-01T00:00:00Z` and
// `to: '2026-05-05'` as `2026-05-05T23:59:59.999Z` (UTC). Any `accountRequestedAt`
// timestamp in CDT (UTC-5) falls into the wrong day — e.g. an order placed Apr 30
// 8pm Austin (= May 1 01:00 UTC) gets included in a "May 1" query, while an order
// placed May 5 8pm Austin (= May 6 01:00 UTC) gets excluded. Was reported by QA as
// "FROM May/1 TO May/5 shows orders Apr/30 to May/04".
//
// We use moment.tz directly (not the Austin wrapper) because the wrapper parses in
// the browser's local timezone and only THEN shifts to display in Chicago — which
// silently produces a one-day shift for any user whose browser is set to UTC. The
// `moment.tz(string, format, zone)` form parses the wall-clock string AS Chicago
// time directly, so the resulting instant is always May 1 00:00 Austin no matter
// where the operator is.
function dateRangeForWire(value) {
  if (!value || typeof value !== 'object') return value;
  const toIso = (raw, edge) => {
    if (raw == null || raw === '') return null;
    if (typeof raw !== 'string' || !ISO_DATE_ONLY_PATTERN.test(raw)) return raw;
    const m = moment.tz(raw, 'YYYY-MM-DD', AUSTIN_TZ);
    if (!m.isValid()) return raw;
    return (edge === 'end' ? m.endOf('day') : m.startOf('day')).toISOString();
  };
  const wire = {};
  if (value.from != null && value.from !== '') {
    const iso = toIso(value.from, 'start');
    if (iso != null) wire.from = iso;
  }
  if (value.to != null && value.to !== '') {
    const iso = toIso(value.to, 'end');
    if (iso != null) wire.to = iso;
  }
  return Object.keys(wire).length > 0 ? wire : null;
}

// Unwraps the wire format into the draft shape inputs expect.
//   - text: { contains } → string (raw input value)
//   - dateRange: ISO timestamps OR YYYY-MM-DD → YYYY-MM-DD strings (matches
//     <input type="date">). Same path for both wire formats.
// All other filter types use the same shape on the wire and in the draft.
function unwrapForDraft(filterType, value) {
  if (
    filterType === SR_TABLE_COLUMN_FILTER_TYPE_TEXT &&
    value &&
    typeof value === 'object' &&
    typeof value.contains === 'string'
  ) {
    return value.contains;
  }
  if (filterType === SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE) {
    return dateRangeFromWire(value);
  }
  return value;
}

// Wraps the user's input into the backend's expected filter shape on commit.
//   - text: trimmed → { contains } envelope so the backend does ILIKE substring search
//     (other data tables expect equality when they receive a raw string)
//   - dateRange: YYYY-MM-DD inputs → either YYYY-MM-DD (DATE columns) or Austin-tz
//     ISO timestamps (TIMESTAMPTZ columns), based on dateInputMode. See the doc
//     comment on the SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_* constants for the
//     full reasoning. Default is 'date' since that's the historical wire format
//     the backend's applyRangeFilter was originally designed for; columns that
//     store true instants (TIMESTAMPTZ) opt in to 'instant'.
function wrapForCommit(filterType, draftValue, dateInputMode) {
  if (filterType === SR_TABLE_COLUMN_FILTER_TYPE_TEXT) {
    const trimmed = typeof draftValue === 'string' ? draftValue.trim() : '';
    return trimmed.length > 0 ? { contains: trimmed } : null;
  }
  if (filterType === SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE) {
    if (!hasValue(filterType, draftValue)) return null;
    if (dateInputMode === SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_INSTANT) {
      return dateRangeForWire(draftValue);
    }
    // 'date' mode: send YYYY-MM-DD as-is. Backend's applyRangeFilter pushes the
    // upper bound to end-of-day for inclusive matching.
    const wire = {};
    if (draftValue.from != null && draftValue.from !== '') wire.from = draftValue.from;
    if (draftValue.to != null && draftValue.to !== '') wire.to = draftValue.to;
    return Object.keys(wire).length > 0 ? wire : null;
  }
  return hasValue(filterType, draftValue) ? draftValue : null;
}

function CategoricalFilter({ options, draftValue, setDraftValue, showBlankOption }) {
  const selected = Array.isArray(draftValue) ? draftValue : [];
  const toggle = (optionValue) => {
    setDraftValue(
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };
  const isBlankSelected = selected.includes(null);
  // "(blank)" is a built-in categorical option that maps to `null` on the wire. The backend
  // (DataTable.applyFilters) splits out `null` entries into an explicit `IS NULL` leg combined
  // via OR with the rest of the array, so users can filter for missing values alongside or
  // instead of concrete ones. Rendered as the first row so it's predictable per Excel-style
  // filter idioms.
  //
  // Columns whose "missing" case is a meaningful domain value rather than an absence should
  // opt out via showBlankOption={false} and model that case as an explicit option instead —
  // see the Set By column on SrUtilityProfitAddressesDataTable, where a NULL means "excluded
  // from this filter entirely" and so must not be selectable.
  return (
    <div styleName="srTableColumnFilter-categoricalList">
      {showBlankOption && (
        <button
          key="__blank__"
          type="button"
          styleName={classnames('srTableColumnFilter-categoricalItem', {
            isSelected: isBlankSelected,
          })}
          onClick={() => toggle(null)}
          aria-pressed={isBlankSelected}
        >
          <span
            styleName="srTableColumnFilter-categoricalLabel srTableColumnFilter-categoricalBlank"
          >
            (blank)
          </span>
        </button>
      )}
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        return (
          <button
            key={String(option.value)}
            type="button"
            styleName={classnames('srTableColumnFilter-categoricalItem', { isSelected })}
            onClick={() => toggle(option.value)}
            aria-pressed={isSelected}
            title={typeof option.label === 'string' ? option.label : undefined}
          >
            <span styleName="srTableColumnFilter-categoricalLabel">{option.label}</span>
          </button>
        );
      })}
      {options.length === 0 && (
        <div styleName="srTableColumnFilter-emptyHint">No other options available</div>
      )}
    </div>
  );
}
CategoricalFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      label: PropTypes.node.isRequired,
    }),
  ).isRequired,
  draftValue: PropTypes.array,
  setDraftValue: PropTypes.func.isRequired,
  showBlankOption: PropTypes.bool,
};

CategoricalFilter.defaultProps = {
  showBlankOption: true,
};

// Module-level counter used to mint stable, unique DOM ids per RangeFilter
// instance (React 16 doesn't have useId). Initialized in `useState` below so
// the same id sticks for the lifetime of the component.
let nextRangeFilterUid = 0;

// Range pair (label above input, stacked vertically). Used by both date-range and
// number-range filters so the two share visual structure — clicking either reveals the
// same compact "from/to" or "min/max" pattern.
//
// Each label/input pair gets a unique stable id (htmlFor + id) so
// jsx-a11y/label-has-associated-control is satisfied even though the label
// visually wraps the span+input.
function RangeFilter({ fromInputType, fromKey, toKey, fromLabel, toLabel, draftValue, setDraftValue }) {
  const value = draftValue || {};
  // eslint-disable-next-line no-plusplus
  const [uid] = useState(() => ++nextRangeFilterUid);
  const fromInputId = `srTableColumnFilter-${uid}-${fromKey}`;
  const toInputId = `srTableColumnFilter-${uid}-${toKey}`;
  const parse = (raw) => {
    if (raw === '') return null;
    return fromInputType === 'number' ? Number(raw) : raw;
  };
  const onChangeFrom = (event) => {
    setDraftValue({ ...value, [fromKey]: parse(event.target.value) });
  };
  const onChangeTo = (event) => {
    setDraftValue({ ...value, [toKey]: parse(event.target.value) });
  };
  // For date inputs, surface min/max so each picker can constrain the other.
  const fromExtra = fromInputType === 'date' ? { max: value[toKey] || undefined } : {};
  const toExtra = fromInputType === 'date' ? { min: value[fromKey] || undefined } : {};
  return (
    <div styleName="srTableColumnFilter-rangeRow">
      <label htmlFor={fromInputId} styleName="srTableColumnFilter-rangeField">
        <span styleName="srTableColumnFilter-rangeLabel">{fromLabel}</span>
        <input
          id={fromInputId}
          type={fromInputType}
          styleName="srTableColumnFilter-rangeInput"
          value={value[fromKey] == null ? '' : value[fromKey]}
          onChange={onChangeFrom}
          {...fromExtra}
        />
      </label>
      <label htmlFor={toInputId} styleName="srTableColumnFilter-rangeField">
        <span styleName="srTableColumnFilter-rangeLabel">{toLabel}</span>
        <input
          id={toInputId}
          type={fromInputType}
          styleName="srTableColumnFilter-rangeInput"
          value={value[toKey] == null ? '' : value[toKey]}
          onChange={onChangeTo}
          {...toExtra}
        />
      </label>
    </div>
  );
}
RangeFilter.propTypes = {
  fromInputType: PropTypes.oneOf(['date', 'number']).isRequired,
  fromKey: PropTypes.string.isRequired,
  toKey: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  draftValue: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  setDraftValue: PropTypes.func.isRequired,
};

function TextFilter({ draftValue, setDraftValue, placeholder }) {
  return (
    <input
      type="text"
      styleName="srTableColumnFilter-textInput"
      // autoFocus is intentional UX: the popover opens specifically so the
      // operator can type — focusing the input lets them start typing without
      // an extra click. Trade-off accepted vs. the jsx-a11y/no-autofocus rule.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={draftValue || ''}
      placeholder={placeholder}
      onChange={(event) => setDraftValue(event.target.value)}
    />
  );
}
TextFilter.propTypes = {
  draftValue: PropTypes.string,
  setDraftValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const SrTableColumnFilter = ({
  filterType,
  filterOptions,
  showBlankOption,
  triggerVariant,
  textPlaceholder,
  dateInputMode,
  value,
  onChange,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Draft value is what the user is editing inside the popover.
  // It only commits to onChange (and triggers a data refetch) when Apply is clicked,
  // so a user toggling several rows only kicks off one network round-trip.
  const [draftValue, setDraftValue] = useState(() => unwrapForDraft(filterType, value));
  // 'right' anchors the popover's right edge to the trigger (default — opens to the
  // left of the button so right-side columns stay onscreen). 'left' is the runtime
  // flip applied when right-anchoring would push the popover off the viewport's left
  // edge — happens for left-side columns like Sales Rep where Kyle saw the menu clip.
  const [popoverAnchor, setPopoverAnchor] = useState('right');
  const containerRef = useRef(null);
  const popoverRef = useRef(null);

  const isActive = hasValue(filterType, value);
  const showActiveSummary = isActive && triggerVariant !== SR_TABLE_COLUMN_FILTER_TRIGGER_BARE;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocumentClick = (event) => {
      if (!containerRef.current || !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [isOpen]);

  // Flip the popover horizontally if the default right-anchor would clip past the
  // viewport's left edge. Runs synchronously after layout so the user never sees the
  // clipped position. The reset to 'right' lives in `open` (below), so each new
  // open-cycle starts fresh and re-evaluates against the current viewport.
  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current) return;
    const rect = popoverRef.current.getBoundingClientRect();
    if (rect.left < 8) {
      setPopoverAnchor('left');
    }
  }, [isOpen]);

  const open = useCallback(
    (event) => {
      event.stopPropagation();
      setDraftValue(unwrapForDraft(filterType, value));
      setPopoverAnchor('right');
      setIsOpen(true);
    },
    [filterType, value],
  );

  const close = useCallback((event) => {
    if (event) {
      event.stopPropagation();
    }
    setIsOpen(false);
  }, []);

  const apply = useCallback(
    (event) => {
      event.stopPropagation();
      onChange(wrapForCommit(filterType, draftValue, dateInputMode));
      setIsOpen(false);
    },
    [draftValue, filterType, dateInputMode, onChange],
  );

  const clear = useCallback(
    (event) => {
      event.stopPropagation();
      setDraftValue(null);
      // No-op when nothing is set, but we still close the popover so the user gets
      // immediate feedback. Avoids the disabled-button "is this broken?" reaction
      // Kyle flagged in UAT.
      if (isActive) {
        onChange(null);
      }
      setIsOpen(false);
    },
    [isActive, onChange],
  );

  const summary = useMemo(() => {
    if (!isActive) return null;
    switch (filterType) {
      case SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL:
        return value.length === 1 ? '1 selected' : `${value.length} selected`;
      case SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE: {
        // Wire format is Austin-timezone ISO timestamps; render in Austin time so
        // the summary matches the displayed date column values exactly.
        const fmt = (s) => (s ? momentWithAustinTimezone(s).format('MM/DD/YY') : '*');
        return `${fmt(value.from)} – ${fmt(value.to)}`;
      }
      case SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE: {
        const fmt = (n) => (n == null ? '*' : String(n));
        return `${fmt(value.min)} – ${fmt(value.max)}`;
      }
      case SR_TABLE_COLUMN_FILTER_TYPE_TEXT:
        // Wire format is { contains }, but defensively handle a raw string from any
        // legacy caller still passing the pre-tagged shape.
        return typeof value === 'object' ? value.contains : value;
      default:
        return null;
    }
  }, [filterType, isActive, value]);

  return (
    <span
      styleName={classnames('srTableColumnFilter', {
        hasBareTrigger: triggerVariant === SR_TABLE_COLUMN_FILTER_TRIGGER_BARE,
      })}
      ref={containerRef}
      // Stops clicks anywhere in the filter UI from propagating to the parent <th>,
      // which has its own `onClick` that toggles sort. Without this, mis-clicks on
      // the trigger's whitespace or any popover background would also fire a sort.
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        styleName={classnames('srTableColumnFilter-trigger', {
          isActive,
          isOpen,
          isBare: triggerVariant === SR_TABLE_COLUMN_FILTER_TRIGGER_BARE,
        })}
        onClick={isOpen ? close : open}
        aria-label={ariaLabel || 'Filter column'}
        aria-expanded={isOpen}
        title={ariaLabel || 'Filter column'}
      >
        <span styleName="srTableColumnFilter-icon" aria-hidden="true" />
        {showActiveSummary && <span styleName="srTableColumnFilter-activeDot" aria-hidden="true" />}
        {showActiveSummary && summary && (
          <span styleName="srTableColumnFilter-summary" title={summary}>
            {summary}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          styleName={classnames('srTableColumnFilter-popover', {
            anchorRight: popoverAnchor === 'right',
            anchorLeft: popoverAnchor === 'left',
          })}
          role="dialog"
          ref={popoverRef}
        >
          <div styleName="srTableColumnFilter-body">
            {filterType === SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL && (
              <CategoricalFilter
                options={filterOptions || []}
                draftValue={draftValue}
                setDraftValue={setDraftValue}
                showBlankOption={showBlankOption}
              />
            )}
            {filterType === SR_TABLE_COLUMN_FILTER_TYPE_DATE_RANGE && (
              <RangeFilter
                fromInputType="date"
                fromKey="from"
                toKey="to"
                fromLabel="From"
                toLabel="To"
                draftValue={draftValue}
                setDraftValue={setDraftValue}
              />
            )}
            {filterType === SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE && (
              <RangeFilter
                fromInputType="number"
                fromKey="min"
                toKey="max"
                fromLabel="Min"
                toLabel="Max"
                draftValue={draftValue}
                setDraftValue={setDraftValue}
              />
            )}
            {filterType === SR_TABLE_COLUMN_FILTER_TYPE_TEXT && (
              <TextFilter
                draftValue={draftValue}
                setDraftValue={setDraftValue}
                placeholder={textPlaceholder}
              />
            )}
          </div>
          <div styleName="srTableColumnFilter-actions">
            <button
              type="button"
              styleName="srTableColumnFilter-clearButton"
              onClick={clear}
            >
              Clear
            </button>
            <button
              type="button"
              styleName="srTableColumnFilter-applyButton"
              onClick={apply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

SrTableColumnFilter.displayName = 'SrTableColumnFilter';

SrTableColumnFilter.propTypes = {
  filterType: PropTypes.oneOf(SR_TABLE_COLUMN_FILTER_TYPES).isRequired,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
  // Only meaningful when filterType === 'categorical'. Defaults to true so every existing
  // column keeps the built-in "(blank)" pseudo-option. Set false when NULL is not a value a
  // user should be able to select for this column.
  showBlankOption: PropTypes.bool,
  // Chrome around the trigger button. See the SR_TABLE_COLUMN_FILTER_TRIGGER_* constants.
  triggerVariant: PropTypes.oneOf(SR_TABLE_COLUMN_FILTER_TRIGGER_VARIANTS),
  textPlaceholder: PropTypes.string,
  // Only meaningful when filterType === 'dateRange'. See the doc comment on the
  // SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_* constants for guidance on which
  // mode each column should use.
  dateInputMode: PropTypes.oneOf(SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODES),
  // Filter value shape on the wire varies by type:
  //  - categorical: array of selected option values (with `null` representing the
  //    built-in "(blank)" pseudo-option)
  //  - dateRange:   { from?, to? } where each is either a YYYY-MM-DD string
  //                 (dateInputMode='date', for DATE columns) or an Austin-tz ISO
  //                 timestamp (dateInputMode='instant', for TIMESTAMPTZ columns).
  //  - numberRange: { min?: number, max?: number }
  //  - text:        { contains: string }
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

SrTableColumnFilter.defaultProps = {
  dateInputMode: SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_DATE,
  showBlankOption: true,
  triggerVariant: SR_TABLE_COLUMN_FILTER_TRIGGER_CHIP,
};

export default SrTableColumnFilter;
